import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';

import { SurveysService } from '@app/services/surveys.service';
import {
  addInspectionSurveyTemplate,
  loadSurveyTemplates,
  loadSurveyTemplatesFailure,
  loadSurveyTemplatesSuccess,
  saveInspectionSurveyTemplateFailure,
  saveInspectionSurveyTemplateSuccess,
  saveSurveyTemplate,
  saveSurveyTemplateFailure,
  saveSurveyTemplateSuccess,
  updateInspectionSurveyTemplate,
} from '@main-application/survey-creator/actions/surveys.actions';
import { AppRoutes } from '@shared/constants/app-routes.const';
import { TEMPLATE_VIEWS } from '@template/models/template-views';
import { loadAllUserTemplates } from '@template/store/template.actions';
import { SnackbarErrorMessage } from '@ui-components/components/customized-snackbar/snackbar-message.enum';
import { SnackbarService } from '@ui-components/components/customized-snackbar/snackbar.service';

@Injectable()
export class SurveysEffects {
  loadSurveyTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadSurveyTemplates),
      switchMap(() =>
        this.surveysService.getSurveyTemplates().pipe(
          map(templates => {
            return loadSurveyTemplatesSuccess({ templates });
          }),
          catchError(error => {
            this.snackbarService.error(SnackbarErrorMessage.ErrorSurveysTemplates);
            return of(loadSurveyTemplatesFailure({ error }));
          })
        )
      )
    )
  );

  saveSurveyTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(saveSurveyTemplate),
      switchMap(action =>
        this.surveysService.saveTemplate(action.id, action.template).pipe(
          map(templates => {
            this.snackbarService.success(SnackbarErrorMessage.SuccessSavingSurveyTemplate);
            return saveSurveyTemplateSuccess({ templates });
          }),
          catchError(error => {
            this.snackbarService.error(SnackbarErrorMessage.ErrorSavingSurveyTemplate);
            return of(saveSurveyTemplateFailure({ error }));
          })
        )
      )
    )
  );

  addInspectionSurveyTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addInspectionSurveyTemplate),
      switchMap(action =>
        this.surveysService.add(action.survey).pipe(
          tap(id => {
            this.router
              .navigate([AppRoutes.SETTINGS_TEMPLATE, action.survey.inspectionTemplateId, TEMPLATE_VIEWS.AREA, id], {
                queryParamsHandling: 'preserve',
              })
              .then();
          }),
          map(id => {
            this.snackbarService.success(SnackbarErrorMessage.SuccessSavingSurveyTemplate);
            return saveInspectionSurveyTemplateSuccess({ surveyId: id });
          }),
          mergeMap(() => [loadAllUserTemplates()]),
          catchError(error => {
            this.snackbarService.error(SnackbarErrorMessage.ErrorSavingSurveyTemplate);
            return of(saveInspectionSurveyTemplateFailure({ error }));
          })
        )
      )
    )
  );

  updateInspectionSurveyTemplate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateInspectionSurveyTemplate),
      switchMap(action =>
        this.surveysService.update(action.survey).pipe(
          map(() => {
            this.snackbarService.success(SnackbarErrorMessage.SuccessSavingSurveyTemplate);
            return saveInspectionSurveyTemplateSuccess({ surveyId: action.survey.id });
          }),
          mergeMap(() => [loadAllUserTemplates()]),
          catchError(error => {
            this.snackbarService.error(SnackbarErrorMessage.ErrorSavingSurveyTemplate);
            return of(saveInspectionSurveyTemplateFailure({ error }));
          })
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private router: Router,
    private surveysService: SurveysService,
    private snackbarService: SnackbarService
  ) {}
}
